import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { FilePlus2, FileText, Calendar, Cpu, ArrowUpRight, User as UserIcon } from "lucide-react";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const modelLabel = (k) => k === "gpt-5.2" ? "GPT-5.2" : k === "claude-sonnet-4.5" ? "Claude Sonnet 4.5" : k;

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(isAdmin ? "all" : "mine"); // admins default to global view

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = scope === "all" && isAdmin ? "/admin/rattrapages" : "/rattrapages";
        const { data } = await api.get(url);
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, isAdmin]);

  const showAuthorCol = scope === "all" && isAdmin;

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1400px]" data-testid="dashboard-page">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="label-xs mb-2">Tableau de bord</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {showAuthorCol ? "Tous les rattrapages" : "Vos rattrapages"}
            </h1>
            <p className="text-slate-600 mt-2 text-sm max-w-xl">
              {showAuthorCol
                ? "Vue administrateur : tous les sujets QCM créés par le staff pédagogique."
                : "Tous les sujets QCM générés par votre équipe pédagogique, prêts à être déposés sur Microsoft Forms."}
            </p>
          </div>
          <Link
            to="/creer"
            data-testid="dashboard-create-btn"
            className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            <FilePlus2 size={16} />
            Créer un rattrapage
          </Link>
        </div>

        {isAdmin && (
          <div className="mt-6 inline-flex items-center bg-white border border-slate-200 rounded-sm p-0.5" data-testid="scope-switcher">
            <button
              onClick={() => setScope("mine")}
              data-testid="scope-mine"
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                scope === "mine" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              Mes rattrapages
            </button>
            <button
              onClick={() => setScope("all")}
              data-testid="scope-all"
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                scope === "all" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              Tous (équipe)
            </button>
          </div>
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <StatCard label={showAuthorCol ? "Total équipe" : "Rattrapages créés"} value={items.length} testId="stat-total" />
          <StatCard label="Format" value="4 Q · 1h" testId="stat-format" />
          <StatCard label="Modèles IA" value="GPT-5.2 · Claude 4.5" testId="stat-models" small />
        </div>

        {/* Table */}
        <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="label-xs">{showAuthorCol ? "Historique global" : "Historique"}</div>
            <div className="text-xs text-slate-500">{items.length} entrée(s)</div>
          </div>

          {loading ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500" data-testid="dashboard-loading">Chargement…</div>
          ) : items.length === 0 ? (
            <EmptyState />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="text-left px-5 py-3 font-semibold">Module</th>
                  <th className="text-left px-5 py-3 font-semibold">Titre</th>
                  {showAuthorCol && <th className="text-left px-5 py-3 font-semibold">Auteur</th>}
                  <th className="text-left px-5 py-3 font-semibold">Modèle</th>
                  <th className="text-left px-5 py-3 font-semibold">Date</th>
                  <th className="text-right px-5 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`row-${r.id}`}>
                    <td className="px-5 py-4 font-semibold text-slate-900">{r.module}</td>
                    <td className="px-5 py-4 text-slate-700">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-slate-400" />
                        <span className="truncate max-w-xs">{r.title}</span>
                      </div>
                    </td>
                    {showAuthorCol && (
                      <td className="px-5 py-4 text-slate-700">
                        <div className="flex items-center gap-1.5">
                          <UserIcon size={12} className="text-slate-400" />
                          <div>
                            <div className="text-sm">{r.author_name}</div>
                            <div className="text-[11px] text-slate-500">{r.author_email}</div>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-blue-50 text-[#2563eb] rounded-sm font-semibold">
                        <Cpu size={11} /> {modelLabel(r.ai_model)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-slate-400" />
                        {fmtDate(r.created_at)}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/rattrapage/${r.id}`}
                        data-testid={`open-${r.id}`}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:text-[#2563eb]"
                      >
                        Ouvrir <ArrowUpRight size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, testId, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testId}>
      <div className="label-xs">{label}</div>
      <div className={`font-display ${small ? "text-lg" : "text-3xl"} font-semibold text-slate-900 mt-2 tracking-tight`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-16 text-center" data-testid="dashboard-empty">
      <div className="mx-auto w-12 h-12 rounded-sm bg-slate-100 flex items-center justify-center mb-4">
        <FilePlus2 size={20} className="text-slate-500" />
      </div>
      <div className="font-display text-lg font-semibold text-slate-900">Aucun rattrapage pour l'instant</div>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        Commencez par déposer vos exemples passés dans la bibliothèque, puis générez votre premier sujet.
      </p>
      <Link
        to="/creer"
        className="inline-flex items-center gap-2 mt-5 bg-[#0f172a] text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-slate-800"
      >
        <FilePlus2 size={14} /> Créer un rattrapage
      </Link>
    </div>
  );
}
